import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Phone, Mail, MessageCircle,
  ClipboardList, History, FileText, Sliders, Thermometer,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";
import CustomFieldsPanel from "./CustomFieldsPanel";
import OverviewTab from "./lead-drawer/OverviewTab";
import HistoryTab from "./lead-drawer/HistoryTab";
import NotesTab from "./lead-drawer/NotesTab";
import WhatsAppTab from "./lead-drawer/WhatsAppTab";
import { ClaireEmailDraftModal } from "./ContactActions";
import WinningStrategy from "./WinningStrategy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMP_COLOR = {
  QUENTE: "#DC2626",
  MORNO:  "#F97316",
  FRIO:   "#16A34A",
};

function fmtDateInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch (_e) { return ""; }
}

export default function LeadDetailDrawer({ open, lead, onClose, onChanged, onWhatsApp }) {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("overview");
  const [draftOpen, setDraftOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [followup, setFollowup] = useState("");
  const [notes, setNotes] = useState("");
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  // Iter113 — Sunbiz-sourced leads live in their own collection and use a
  // dedicated enrich endpoint. Detect via the `sunbiz` block injected by
  // Sunbiz.jsx::sunbizToLead().
  const sunbizDoc = lead?.sunbiz?.document_number;

  useEffect(() => {
    if (!open || !lead) return;
    setTab("overview");
    setFollowup(fmtDateInput(lead.next_followup_at));
    setNotes(lead.crm_notes || "");
  }, [open, lead]);

  useEffect(() => {
    if (!open || !lead?.id || tab !== "history") return;
    let cancelled = false;
    setLoadingHistory(true);
    axios.get(`${API}/leads/${lead.id}/history`)
      .then(({ data }) => { if (!cancelled) setHistory(data); })
      .catch(() => { if (!cancelled) setHistory({ items: [] }); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, [open, lead?.id, tab]);

  if (!open || !lead) return null;

  const tempColor = TEMP_COLOR[lead.badge] || TEMP_COLOR.MORNO;
  const phoneRaw = lead.phone_international || lead.phone || "";
  const telHref = phoneRaw ? `tel:${phoneRaw.replace(/[^+\d]/g, "")}` : null;
  const mailHref = lead.email ? `mailto:${lead.email}` : null;

  const saveFollowup = async () => {
    setSavingFollowup(true);
    try {
      const iso = followup ? new Date(`${followup}T09:00:00`).toISOString() : "";
      await axios.patch(`${API}/leads/${lead.id}`, { next_followup_at: iso });
      toast.success(t("ldd.followup_saved"));
      onChanged?.();
    } catch (_e) {
      toast.error(t("ldd.followup_failed"));
    } finally { setSavingFollowup(false); }
  };

  const clearFollowup = async () => {
    setFollowup("");
    setSavingFollowup(true);
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { next_followup_at: "" });
      toast.success(t("ldd.followup_saved"));
      onChanged?.();
    } catch (_e) {
      toast.error(t("ldd.followup_failed"));
    } finally { setSavingFollowup(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { crm_notes: notes });
      toast.success(t("ldd.notes_saved"));
      onChanged?.();
    } catch (_e) {
      toast.error(t("ldd.followup_failed"));
    } finally { setSavingNotes(false); }
  };

  const enrichDecisionMaker = async () => {
    setEnriching(true);
    try {
      const lang = (window.localStorage.getItem("alp.lang") === "pt") ? "pt" : "en";
      // Iter113 — Sunbiz leads route to a dedicated endpoint that knows the
      // sunbiz_leads schema (company_name, principal_address, officer_name).
      const url = sunbizDoc
        ? `${API}/sunbiz/leads/${sunbizDoc}/enrich-dm`
        : `${API}/leads/${lead.id}/enrich-decision-maker`;
      await axios.post(url, { lang });
      toast.success(lang === "pt" ? "Decisor identificado pela IA" : "Decision-maker identified by AI");
      onChanged?.();
    } catch (_e) {
      toast.error("Enrich failed");
    } finally { setEnriching(false); }
  };

  const saveContact = async ({ phone, email }) => {
    setSavingContact(true);
    try {
      if (sunbizDoc) {
        await axios.patch(`${API}/sunbiz/leads/${sunbizDoc}/contact`, { phone, email });
      } else {
        await axios.patch(`${API}/leads/${lead.id}`, { phone, email });
      }
      toast.success("Contato salvo");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar contato");
    } finally { setSavingContact(false); }
  };

  return (
    <div
      data-testid="lead-detail-drawer"
      className="fixed inset-0 z-40 flex justify-center sm:justify-end items-end sm:items-stretch bg-black/40"
      onClick={onClose}
    >
      <aside
        data-testid="lead-detail-panel"
        className="w-full sm:w-[440px] h-[92vh] sm:h-full rounded-t-2xl sm:rounded-none text-[var(--al-text)] border-t sm:border-t-0 sm:border-l border-[var(--al-border)] shadow-2xl flex flex-col"
        style={{
          backgroundColor: "var(--al-card-bg, #FFFFFF)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-[var(--al-border)] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Thermometer className="w-3.5 h-3.5 shrink-0" style={{ color: tempColor }} />
              <span
                className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded"
                style={{ background: `${tempColor}20`, color: tempColor }}
              >
                {lead.badge || "MORNO"}
              </span>
              <span className="text-[10px] text-[var(--al-text-muted)]">
                {t("ldd.field_score")}: <strong>{lead.digital_score ?? "—"}</strong>/100
              </span>
            </div>
            <h2 className="text-[16px] font-semibold truncate">{lead.name}</h2>
            <p className="text-[12px] text-[var(--al-text-muted)] truncate">
              {lead.sector || "—"}{lead.crm_status ? ` · ${lead.crm_status}` : ""}
            </p>
          </div>
          <button
            type="button"
            data-testid="ldd-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full inline-flex items-center justify-center text-[var(--al-text-muted)] hover:text-[var(--al-text)] hover:bg-[var(--al-bg)] -mr-1"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 pt-3 pb-2 border-b border-[var(--al-border)]">
          <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-2">
            {t("ldd.actions_title")}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="ldd-action-wa"
              disabled={!phoneRaw}
              onClick={() => onWhatsApp?.(lead)}
              title={phoneRaw ? t("ldd.action_wa") : t("ldd.no_phone")}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#25D366] text-[#25D366] hover:bg-[#25D36610] text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-3.5 h-3.5" /> {t("ldd.action_wa")}
            </button>
            <a
              href={telHref || "#"}
              data-testid="ldd-action-call"
              onClick={(e) => { if (!telHref) e.preventDefault(); }}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[12px] font-medium ${telHref ? "border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F610]" : "border-[var(--al-border)] text-[var(--al-text-muted)] opacity-40 cursor-not-allowed"}`}
              title={telHref ? phoneRaw : t("ldd.no_phone")}
            >
              <Phone className="w-3.5 h-3.5" /> {t("ldd.action_call")}
            </a>
            <button
              type="button"
              data-testid="ldd-action-email"
              disabled={!mailHref}
              onClick={() => setDraftOpen(true)}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[12px] font-medium ${mailHref ? "border-[#A855F7] text-[#A855F7] hover:bg-[#A855F710]" : "border-[var(--al-border)] text-[var(--al-text-muted)] opacity-40 cursor-not-allowed"}`}
              title={mailHref ? `Claire · ${lead.email}` : t("ldd.no_email")}
            >
              <Mail className="w-3.5 h-3.5" /> {t("ldd.action_email")}
            </button>
            <ClaireEmailDraftModal open={draftOpen} onClose={() => setDraftOpen(false)}
              email={lead.email} isPt={lang === "pt"}
              context={{ name: lead.decision_maker?.name, company: lead.name,
                sector: lead.sector, address: lead.address, website: lead.website,
                role: lead.decision_maker?.role }} />
          </div>
        </div>

        <nav className="flex border-b border-[var(--al-border)] text-[12px]">
          {[
            { id: "overview", icon: ClipboardList, label: t("ldd.tab_overview") },
            { id: "history",  icon: History,       label: t("ldd.tab_history")  },
            { id: "notes",    icon: FileText,      label: t("ldd.tab_notes")    },
            { id: "whatsapp", icon: History,       label: "WhatsApp" },
            { id: "custom",   icon: Sliders,       label: "Custom" },
          ].map((it) => {
            const active = tab === it.id;
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                type="button"
                data-testid={`ldd-tab-${it.id}`}
                onClick={() => setTab(it.id)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 ${active ? "text-[#3B82F6] border-b-2 border-[#3B82F6] font-semibold" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {it.label}
              </button>
            );
          })}
        </nav>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-[12.5px]">
          {tab === "overview" && (
            <>
              <WinningStrategy leadType="b2b" leadId={lead.id} isPt={lang === "pt"} />
              <OverviewTab
              lead={lead} t={t} phoneRaw={phoneRaw}
              enriching={enriching} enrichDecisionMaker={enrichDecisionMaker}
              followup={followup} setFollowup={setFollowup}
              savingFollowup={savingFollowup} saveFollowup={saveFollowup}
              clearFollowup={clearFollowup}
              savingContact={savingContact} saveContact={saveContact}
              onChanged={onChanged}
            />
            </>
          )}

          {tab === "history" && (
            <HistoryTab history={history} loadingHistory={loadingHistory} t={t} />
          )}

          {tab === "notes" && (
            <NotesTab notes={notes} setNotes={setNotes}
              savingNotes={savingNotes} saveNotes={saveNotes} t={t} />
          )}

          {tab === "whatsapp" && <WhatsAppTab lead={lead} />}

          {tab === "custom" && (
            <CustomFieldsPanel lead={lead} onChanged={onChanged} />
          )}
        </div>
      </aside>
    </div>
  );
}
